/**
 * Avatar — initials-based by default, falls back to image if `src` provided.
 *
 *   <Avatar name="Nguyễn Thị Hương" />
 *   <Avatar name="Trần Văn Minh" size={32} color="vehicle" />
 *   <Avatar src="/uploads/u123.jpg" name="Trần Văn Minh" />
 *   <AvatarGroup>...</AvatarGroup>  // stacked avatars
 */

import React from 'react';
import { initials } from '../utils/format';

// Stable color picker — same name always picks the same colour.
// Useful for avatars across the app to feel consistent.
const PALETTE = [
  { bg: 'bg-teal-50',  text: 'text-teal-700' },
  { bg: 'bg-violet-50',  text: 'text-violet-600' },
  { bg: 'bg-sky-50',    text: 'text-blue-700' },
  { bg: 'bg-green-50',  text: 'text-green-700' },
  { bg: 'bg-amber-50',  text: 'text-amber-700' },
  { bg: 'bg-rose-50',text: 'text-rose-600' },
];

function pickColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

export function Avatar({
  name = '',
  src,
  size = 28,
  color,                 // override automatic colour ('primary' | 'vehicle' | ... or full Tailwind classes)
  className = '',
  ring = false,
  status,                // 'online' | 'offline' | 'busy' — small dot in corner
  ...rest
}) {
  const auto = pickColor(name);
  const colorClass = color
    ? (typeof color === 'string' && color.includes(' ') ? color : `bg-${color}-50 text-${color}-700`)
    : `${auto.bg} ${auto.text}`;
  const fontSize = Math.max(10, Math.round(size * 0.42));

  return (
    <span
      className={[
        'relative inline-flex items-center justify-center shrink-0 rounded-full font-semibold leading-none select-none',
        colorClass,
        ring && 'ring-2 ring-white shadow-xs',
        className,
      ].filter(Boolean).join(' ')}
      style={{ width: size, height: size, fontSize }}
      title={name}
      {...rest}
    >
      {src ? (
        <img src={src} alt={name} className="w-full h-full object-cover rounded-full" />
      ) : (
        initials(name)
      )}
      {status && (
        <span
          className={[
            'absolute bottom-0 right-0 rounded-full border-2 border-white',
            status === 'online' && 'bg-green-500',
            status === 'busy'   && 'bg-amber-500',
            status === 'offline'&& 'bg-stone-400',
          ].filter(Boolean).join(' ')}
          style={{ width: Math.max(8, size * 0.28), height: Math.max(8, size * 0.28) }}
        />
      )}
    </span>
  );
}

/** Stack of avatars with overlap. Pass max to cap the visible count. */
export function AvatarGroup({ children, max = 4, size = 24, className = '' }) {
  const items = React.Children.toArray(children);
  const visible = items.slice(0, max);
  const overflow = items.length - visible.length;
  return (
    <div className={['inline-flex items-center', className].join(' ')}>
      {visible.map((child, i) =>
        React.cloneElement(child, {
          key: i,
          size,
          ring: true,
          className: [child.props.className || '', i > 0 && '-ml-2'].filter(Boolean).join(' '),
        })
      )}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center -ml-2 rounded-full bg-stone-100 text-stone-600 ring-2 ring-white text-xs font-semibold"
          style={{ width: size, height: size }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
